import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
});

// Add interceptor to automatically add token from localStorage
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export const setAuthToken = (token) => {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common['Authorization'];
  }
};

// Profile API
export const profileAPI = {
  getProfile: () => api.get('/profile'),
  updatePersonal: (data) => api.put('/profile/personal', data),
  updateCompany: (data) => api.put('/profile/company', data),
  updateBranding: (data) => api.put('/profile/branding', data),
  updatePreferences: (data) => api.put('/profile/preferences', data),
  uploadAvatar: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/profile/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadLogo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/profile/company/logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  changePassword: (data) => api.put('/profile/password', data),
  deleteAccount: () => api.delete('/profile/account'),
  getAPIKeys: () => api.get('/api-keys'),
  createAPIKey: (data) => api.post('/api-keys', data),
  revokeAPIKey: (keyId) => api.delete(`/api-keys/${keyId}`),
  // Onboarding
  getOnboardingStatus: () => api.get('/profile/onboarding/status'),
  completeOnboardingStep1: (data) => api.post('/profile/onboarding/step1', data),
  completeOnboardingStep2: (data) => api.post('/profile/onboarding/step2', data),
  completeOnboardingStep3: (data) => api.post('/profile/onboarding/step3', data),
  skipOnboarding: () => api.post('/profile/onboarding/skip'),
  resetOnboarding: () => api.post('/profile/onboarding/reset'),
};

// SmartFacture API - Enhanced for French e-invoicing 2026
export const smartFactureAPI = {
  // Dashboard
  getDashboard: () => api.get('/smartfacture/dashboard'),
  
  // Invoices CRUD
  getInvoices: () => api.get('/smartfacture/invoices'),
  getInvoice: (id) => api.get(`/smartfacture/invoices/${id}`),
  createInvoice: (data) => api.post('/smartfacture/invoices', data),
  updateInvoiceStatus: (id, status) => api.patch(`/smartfacture/invoices/${id}/status`, null, { params: { status } }),
  
  // PDF Generation
  generatePDF: (id) => api.post(`/smartfacture/invoices/${id}/pdf`),
  generateFacturX: (id) => api.post(`/smartfacture/invoices/${id}/facturx`),
  
  // Reminders
  sendReminder: (invoiceId) => api.post('/smartfacture/reminders/send', null, { params: { invoice_id: invoiceId } }),
  getReminders: () => api.get('/smartfacture/reminders'),
  
  // AI Generation
  generateWithAI: (data) => api.post('/smartfacture/generate-ai', data),
  
  // Compliance (French e-invoicing 2026)
  validateCompliance: (invoiceId) => api.post(`/smartfacture/validate-compliance`, null, { params: { invoice_id: invoiceId } }),
  getComplianceStats: () => api.get('/smartfacture/compliance-stats'),
  getOperationTypes: () => api.get('/smartfacture/reference/operation-types'),
  
  // PPF/PDP Simulation & Submission
  simulatePPF: (invoiceId) => api.post(`/smartfacture/ppf/simulate/${invoiceId}`),
  submitToPPF: (invoiceId, platform = 'ppf') => 
    api.post(`/smartfacture/ppf/submit/${invoiceId}`, null, { params: { platform } }),
  getSimulationHistory: () => api.get('/smartfacture/ppf/simulations'),
  getSubmissionHistory: () => api.get('/smartfacture/ppf/submissions'),
  getSubmissionDetails: (submissionId) => api.get(`/smartfacture/ppf/submission/${submissionId}`),
  
  // Scanner / OCR
  analyzeDocument: (imageBase64, mimeType = 'image/jpeg') => 
    api.post('/smartfacture/scan/analyze', { image_base64: imageBase64, mime_type: mimeType }),
  uploadScan: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/smartfacture/scan/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getScans: () => api.get('/smartfacture/scans'),
  getScan: (id) => api.get(`/smartfacture/scans/${id}`),
  convertScanToInvoice: (scanId) => api.post(`/smartfacture/scans/${scanId}/convert`),
};

// LocalMod API
export const localModAPI = {
  getDashboard: () => api.get('/localmod/dashboard'),
  moderateContent: (content, language = 'en', context = null) => 
    api.post('/localmod/moderate', { content, language, context }),
  moderateBatch: (contents, language = 'en') => 
    api.post('/localmod/moderate/batch', { contents, language }),
  getHistory: () => api.get('/localmod/history'),
  exportCSV: () => api.get('/localmod/export/csv', { responseType: 'blob' }),
};

// Analytics API
export const analyticsAPI = {
  getOverview: () => api.get('/analytics/overview'),
};

// Metrics API  
export const metricsAPI = {
  getBusinessMetrics: () => api.get('/metrics/business-metrics'),
  getConversionFunnel: () => api.get('/metrics/conversion-funnel'),
};

// Subscriptions API
export const subscriptionsAPI = {
  getPlans: () => api.get('/subscriptions/plans'),
  createCheckout: (planId, originUrl, promoCode = null) => 
    api.post('/subscriptions/checkout', { plan_id: planId, origin_url: originUrl, promo_code: promoCode }),
  getCheckoutStatus: (sessionId) => 
    api.get(`/subscriptions/checkout/status/${sessionId}`),
  getCurrentSubscription: () => api.get('/subscriptions/current'),
  cancelSubscription: () => api.post('/subscriptions/cancel'),
  getTransactions: () => api.get('/subscriptions/transactions'),
  validatePromo: (code, planId) => 
    api.post('/subscriptions/validate-promo', { code, plan_id: planId }),
};

// Referral API
export const referralAPI = {
  getMyCode: () => api.get('/referral/my-code'),
  getStats: () => api.get('/referral/stats'),
  checkCode: (code) => api.get(`/referral/check-code/${code}`),
  applyCode: (referralCode) => api.post('/referral/apply', { referral_code: referralCode }),
};

// Clients API
export const clientsAPI = {
  getAll: (search = null) => api.get('/clients', { params: search ? { search } : {} }),
  getById: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
  getInvoices: (id) => api.get(`/clients/${id}/invoices`),
};

// Quotes (Devis) API
export const quotesAPI = {
  getAll: (status = null) => api.get('/quotes', { params: status ? { status } : {} }),
  getById: (id) => api.get(`/quotes/${id}`),
  create: (data) => api.post('/quotes', data),
  updateStatus: (id, status) => api.patch(`/quotes/${id}/status`, null, { params: { status } }),
  convertToInvoice: (id) => api.post(`/quotes/${id}/convert`),
  delete: (id) => api.delete(`/quotes/${id}`),
  getStats: () => api.get('/quotes/stats/summary'),
};

// Payments API
export const paymentsAPI = {
  recordPayment: (invoiceId, data) => api.post(`/payments/${invoiceId}/record-payment`, data),
  getInvoicePayments: (invoiceId) => api.get(`/payments/${invoiceId}/payments`),
  markAsPaid: (invoiceId) => api.patch(`/payments/${invoiceId}/mark-paid`),
  markAsUnpaid: (invoiceId) => api.patch(`/payments/${invoiceId}/mark-unpaid`),
  sendReminder: (invoiceId) => api.post(`/payments/${invoiceId}/send-reminder`),
  getRemindersHistory: () => api.get('/payments/reminders/history'),
  getOverdueInvoices: () => api.get('/payments/overdue'),
  getStats: () => api.get('/payments/stats'),
};

// Shares (Partage comptable) API
export const sharesAPI = {
  // Authenticated endpoints
  create: (data) => api.post('/shares', data),
  getAll: (status = null) => api.get('/shares', { params: status ? { status } : {} }),
  getById: (id) => api.get(`/shares/${id}`),
  revoke: (id) => api.delete(`/shares/${id}`),
  getInvoiceShares: (invoiceId) => api.get(`/shares/invoice/${invoiceId}/shares`),
  // Public endpoints (no auth)
  getPublicInvoice: (token) => axios.get(`${API}/shares/public/${token}`),
  exportPublicCSV: (token) => axios.get(`${API}/shares/public/${token}/export/csv`, { responseType: 'blob' }),
  exportPublicPDF: (token) => axios.get(`${API}/shares/public/${token}/export/pdf`),
};

// Social Proof API
export const socialAPI = {
  getUserCount: () => api.get('/social/user-count'),
};

// Inbox API
export const inboxAPI = {
  upload: (formData) => api.post('/inbox/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getAll: (status = null) => api.get('/inbox', { params: status ? { status } : {} }),
  getStats: () => api.get('/inbox/stats'),
  getById: (id) => api.get(`/inbox/${id}`),
  reprocess: (id) => api.post(`/inbox/${id}/reprocess`),
  linkClient: (id, clientId) => api.post(`/inbox/${id}/link-client`, { client_id: clientId }),
  createInvoice: (id, clientId = null) => api.post(`/inbox/${id}/create-invoice`, { 
    client_id: clientId,
    use_extracted_data: true
  }),
  delete: (id) => api.delete(`/inbox/${id}`),
  archive: (id) => api.post(`/inbox/${id}/archive`),
  // Catégorisation IA
  categorize: (id) => api.post(`/inbox/${id}/categorize`),
  setAccountingCode: (id, compte) => api.put(`/inbox/${id}/accounting-code`, null, { params: { compte } }),
  getCategories: () => api.get('/inbox/categories/list'),
};

// Recurring Invoices API
export const recurringAPI = {
  create: (data) => api.post('/recurring', data),
  getAll: (status = null) => api.get('/recurring', { params: status ? { status } : {} }),
  getById: (id) => api.get(`/recurring/${id}`),
  pause: (id) => api.put(`/recurring/${id}/pause`),
  resume: (id) => api.put(`/recurring/${id}/resume`),
  delete: (id) => api.delete(`/recurring/${id}`),
  generate: (id) => api.post(`/recurring/${id}/generate`),
  getStats: () => api.get('/recurring/stats/summary'),
};

// Accounting/FEC API
export const accountingAPI = {
  // Get stats for period
  getStats: (period = 'month') => api.get('/accounting/stats', { params: { period } }),
  // Get accounting entries
  getEntries: (startDate, endDate, includePurchases = true) => api.get('/accounting/entries', {
    params: { start_date: startDate, end_date: endDate, include_purchases: includePurchases }
  }),
  // Export comptable CSV
  exportFEC: (startDate, endDate, includePurchases = true) => api.get('/accounting/export/fec', {
    params: { start_date: startDate, end_date: endDate, include_purchases: includePurchases },
    responseType: 'blob'
  }),
  // Get plan comptable
  getPlanComptable: () => api.get('/accounting/plan-comptable'),
};

// Trial API
export const trialAPI = {
  getStatus: () => api.get('/trial/status'),
  checkAction: (actionType) => api.post(`/trial/check-action/${actionType}`),
  getConfig: () => api.get('/trial/config'),
};

// Notifications API
export const notificationsAPI = {
  getAll: () => api.get('/notifications'),
  markAsRead: (id) => api.patch(`/notifications/${id}/read`),
  markAllAsRead: () => api.patch('/notifications/read-all'),
  delete: (id) => api.delete(`/notifications/${id}`),
  create: (data) => api.post('/notifications', data),
};

export default api;
